// Apolo IDE — LearningPanel Skills sub-tab (WS L6.3).
//
// Lists auto-generated skills from /api/learning/skills. Lets the user
// toggle status (enable/disable), open the skill in Obsidian, or roll
// back (Brain MCP move → _trash + Worker rollback registry).

import { useState } from 'react';
import { ExternalLink, Loader2, Power, Trash2 } from 'lucide-react';
import { useLearningSkills, toggleSkillStatus } from '../../../../hooks/useLearning.js';
import { rollbackSkill } from '../../../../lib/learning/vaultCommit.js';

const STATUS_TONES = {
  active: 'bg-emerald-900/40 text-emerald-200',
  disabled: 'bg-zinc-800 text-zinc-300',
  rolled_back: 'bg-rose-900/40 text-rose-200',
};

export default function Skills() {
  const { data, loading, error, refetch } = useLearningSkills();
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);

  async function toggle(skill) {
    setBusyId(skill.id);
    setActionError(null);
    const next = skill.status === 'active' ? 'disabled' : 'active';
    const res = await toggleSkillStatus(skill.id, next);
    setBusyId(null);
    if (!res.ok) {
      setActionError(res.error || 'toggle failed');
      return;
    }
    refetch();
  }

  async function openInVault(skill) {
    if (typeof window !== 'undefined' && window.apolo?.openInObsidian) {
      try {
        await window.apolo.openInObsidian(skill.vault_path);
      } catch {
        /* swallow — UI tooltip already implies fire-and-forget */
      }
    }
  }

  async function doRollback(skill) {
    if (!window.confirm(`Rollback skill ${skill.name} para _trash?`)) return;
    setBusyId(skill.id);
    setActionError(null);
    const res = await rollbackSkill(skill);
    setBusyId(null);
    if (!res.ok) {
      setActionError(res.error || 'rollback failed');
      return;
    }
    refetch();
  }

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 p-3 text-[11px] text-apolo-textTertiary" role="status">
        <Loader2 size={12} className="animate-spin" aria-hidden="true" />
        Carregando skills...
      </div>
    );
  }
  if (error) {
    return (
      <div role="alert" className="p-3 text-[11px] text-apolo-error">
        {error}
      </div>
    );
  }

  const skills = data?.data?.skills ?? data?.skills ?? [];

  if (skills.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-1 p-6 text-[11px] text-apolo-textTertiary">
        <p>Nenhuma auto-skill ainda.</p>
        <p className="text-[10px]">Skills aprovadas aparecerão aqui.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-3">
      {actionError && (
        <div role="alert" className="rounded border border-apolo-error/30 bg-apolo-error/10 px-2 py-1 text-[10px] text-apolo-error">
          {actionError}
        </div>
      )}
      <ul aria-label="Auto skills" className="flex flex-col gap-2">
        {skills.map((s) => {
          const tone = STATUS_TONES[s.status] || 'bg-apolo-surface2 text-apolo-textSecondary';
          const isBusy = busyId === s.id;
          const isRolledBack = s.status === 'rolled_back';
          return (
            <li key={s.id} className="rounded border border-apolo-borderSubtle bg-apolo-surface2 p-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[11px] font-medium text-apolo-textPrimary">{s.name}</p>
                  <p className="truncate text-[10px] text-apolo-textTertiary" title={s.vault_path}>
                    {s.vault_path}
                  </p>
                </div>
                <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] ${tone}`}>{s.status}</span>
              </div>
              {!isRolledBack && (
                <div className="mt-2 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => toggle(s)}
                    disabled={isBusy}
                    aria-label={s.status === 'active' ? `Desativar skill ${s.name}` : `Ativar skill ${s.name}`}
                    className="inline-flex items-center gap-1 rounded border border-apolo-borderSubtle bg-apolo-surface1 px-2 py-1 text-[10px] text-apolo-textPrimary transition hover:border-apolo-accent disabled:opacity-50"
                  >
                    {isBusy ? <Loader2 size={10} className="animate-spin" aria-hidden="true" /> : <Power size={10} aria-hidden="true" />}
                    {s.status === 'active' ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    type="button"
                    onClick={() => openInVault(s)}
                    aria-label={`Abrir ${s.name} no Obsidian`}
                    className="inline-flex items-center gap-1 rounded border border-apolo-borderSubtle bg-apolo-surface1 px-2 py-1 text-[10px] text-apolo-textPrimary transition hover:border-apolo-accent"
                  >
                    <ExternalLink size={10} aria-hidden="true" />
                    Open
                  </button>
                  <button
                    type="button"
                    onClick={() => doRollback(s)}
                    disabled={isBusy}
                    aria-label={`Rollback skill ${s.name}`}
                    className="inline-flex items-center gap-1 rounded border border-apolo-borderSubtle bg-apolo-surface1 px-2 py-1 text-[10px] text-apolo-error transition hover:border-apolo-error disabled:opacity-50"
                  >
                    <Trash2 size={10} aria-hidden="true" />
                    Rollback
                  </button>
                </div>
              )}
              {isRolledBack && s.trash_vault_path && (
                <p className="mt-1 text-[10px] text-apolo-textTertiary">
                  Trash: <code>{s.trash_vault_path}</code>
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
